import { useQuery, useReactiveVar } from '@apollo/client';
import {
  DirectionalHint,
  makeStyles,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { Handle, Position } from '@xyflow/react';
import clsx from 'clsx';
import { useCommonStyles } from 'common/styles';
import React from 'react';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { CONDITION_NODE_WIDTH } from '../../../constants';
import { useStyles } from '../../../index.styles';
import { movingNodeId } from '../../ApproverLevels';
import { ConditionDataProps, setFlowLayoutState } from '../../Conditions';
import { Body } from './body';
import { DeleteCondition } from './deleteConditions';
import { UpdateCondition } from './updateCondition';
import APPROVAL_TYPE_DATA from '../../../../ApprovalSetupFlowCommonData.graphql';
// @ts-ignore
export const ConditionNode: React.FC = ({
  data,
  isConnectable,
}: {
  data: ConditionDataProps;
  isConnectable: boolean;
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const flowLayoutState = useReactiveVar(setFlowLayoutState);

  const { data: approvalTypes } = useQuery<ApprovalSetupFlowCommonData>(
    APPROVAL_TYPE_DATA,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const movingNode = movingNodeId();

  return (
    <Stack style={{ opacity: movingNode && movingNode !== data.id ? 0.5 : 1 }}>
      <Handle
        type="target"
        position={
          flowLayoutState === 'horizontal' ? Position.Left : Position.Top
        }
        isConnectable={isConnectable}
      />
      <TooltipHost
        content={
          <Stack tokens={{ childrenGap: 16 }} style={{ padding: 5 }}>
            {data?._infoLines?.infoLine1 && (
              <Text variant="small">{data._infoLines.infoLine1}</Text>
            )}
            {data?._infoLines?.infoLine2 && (
              <Text variant="small">{data._infoLines.infoLine2}</Text>
            )}
            {data?._infoLines?.infoLine3 && (
              <Text variant="small">{data._infoLines.infoLine3}</Text>
            )}
            {data?._infoLines?.infoLine4 && (
              <Text variant="small">{data._infoLines.infoLine4}</Text>
            )}
          </Stack>
        }
        directionalHint={DirectionalHint.bottomCenter}
      >
        <Stack className={styles.approvalNodeContainer}>
          <Stack
            horizontalAlign="space-between"
            horizontal
            className={styles.approvalNodeHeader}
          >
            <Stack className={styles.approvalNodeTitle}>
              <Text
                nowrap
                style={{ maxWidth: CONDITION_NODE_WIDTH - 100 }}
                className={clsx(
                  commonStyles.colorThemePrimary,
                  commonStyles.bold
                )}
              >
                {data.description}
              </Text>
            </Stack>

            <Stack horizontal verticalAlign="center">
              <UpdateCondition data={data} approvalTypes={approvalTypes} />
              <DeleteCondition data={data} />
            </Stack>
          </Stack>
          <Body data={data} />
        </Stack>
      </TooltipHost>
      <Handle
        type="source"
        position={
          flowLayoutState === 'horizontal' ? Position.Right : Position.Bottom
        }
        isConnectable={isConnectable}
      />
    </Stack>
  );
};

export const useConditionInfoStyles = makeStyles((theme) => ({
  calloutContainer: {
    backgroundColor: theme.palette.neutralLighterAlt,
  },
  updateConditionCalloutContainer: {
    minWidth: 550,
    width: 400,
    maxHeight: 500,
    overflow: 'hidden',
    backgroundColor: theme.palette.neutralLighterAlt,
  },
}));
